// ─── storage.js — Centralized localStorage registry ──────────────────────────
// All localStorage keys live here. Never use raw string keys elsewhere.
//
// Usage:
//   import { LS } from "../lib/storage.js";
//   const view = LS.get(LS.DASH_VIEW, "month");
//   LS.set(LS.DASH_VIEW, "week");
//   LS.setJson(LS.DASH_MKTS, ["amazon"]);
//   const mkts = LS.getJson(LS.DASH_MKTS, "all");

// ─── Key registry ─────────────────────────────────────────────────────────────
// App-level
export const BRAND        = "sf_brand";
export const THEME_MODE   = "sf_theme_mode";

// Dashboard
export const DASH_VIEW    = "sf_dash_view";
export const DASH_FROM    = "sf_dash_from";
export const DASH_PERIOD  = "sf_dash_period";
export const DASH_PANEL   = "sf_dash_panel";
export const DASH_DEC     = "sf_dash_dec";
export const DASH_ORD     = "sf_dash_ord";
export const DASH_MKTS    = "sf_dash_mkts";

// Targets
export const TARGETS_DANGER_ON  = "sf_targets_danger_on";
export const TARGETS_DANGER_PCT = "sf_targets_danger_pct";

// Right panel / balance
export const PAY_RANGE = "sf_pay_range";

// Cash Calendar
export const CC_SELMKTS = "sf_cc_selmkts";

// Supplier Orders
export const ORDERS_SIMPLE_MODE = "sf_orders_simple";

// Planner
export const PLAN_VIEW    = "sf_plan_view";
export const PLAN_SCROLL  = "sf_plan_scroll";
export const PLAN_CATS    = "sf_plan_cats";

// Markets
export const SELECTED_MKT_ID = "sf_selected_mkt_id";

// Reminders
export const REMINDERS_KEY = "sf_reminders";

// Profile picture
export const PROFILE_PIC = "sf_profile_pic";

// Products page (Phase 1 of the table redesign)
export const PROD_COL_ORDER  = "sf_products_col_order";   // string[] of column ids
export const PROD_COL_HIDDEN = "sf_products_col_hidden";  // string[] of column ids
export const PROD_SORT       = "sf_products_sort";        // { field, dir }
export const PROD_MKT_FILTER = "sf_products_mkt";         // mkt id or "all"
export const PROD_SEARCH     = "sf_products_search";      // string
export const PROD_EXPANDED   = "sf_products_expanded";    // mkt|product id or null

// ─── Typed helpers ────────────────────────────────────────────────────────────

/** Read a string value. Returns `fallback` if the key is missing. */
export function get(key, fallback = "") {
  try {
    const v = localStorage.getItem(key);
    return v !== null ? v : fallback;
  } catch {
    return fallback;
  }
}

/** Write a string value. */
export function set(key, value) {
  try { localStorage.setItem(key, String(value)); } catch {}
}

/** Read a boolean stored as "true" / "false". */
export function getBool(key, fallback = false) {
  const v = get(key, null);
  if (v === null) return fallback;
  return v === "true";
}

/** Write a boolean as "true" / "false". */
export function setBool(key, value) {
  set(key, value ? "true" : "false");
}

/** Read a float. Returns `fallback` if missing or NaN. */
export function getFloat(key, fallback = 0) {
  const v = parseFloat(get(key, ""));
  return isNaN(v) ? fallback : v;
}

/** Write a float. */
export function setFloat(key, value) {
  set(key, String(value));
}

/** Read a JSON value. Returns `fallback` on missing or parse error. */
export function getJson(key, fallback = null) {
  try {
    const v = localStorage.getItem(key);
    return v !== null ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
}

/** Write a JSON-serializable value. */
export function setJson(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

/** Remove a key. */
export function remove(key) {
  try { localStorage.removeItem(key); } catch {}
}

// ─── Convenience namespace export ─────────────────────────────────────────────
// Allows: import { LS } from "../lib/storage.js"; LS.get(LS.DASH_VIEW, "month")
export const LS = {
  // Keys
  BRAND, THEME_MODE,
  DASH_VIEW, DASH_FROM, DASH_PERIOD, DASH_PANEL, DASH_DEC, DASH_ORD, DASH_MKTS,
  TARGETS_DANGER_ON, TARGETS_DANGER_PCT,
  PAY_RANGE, CC_SELMKTS,
  ORDERS_SIMPLE_MODE,
  PLAN_VIEW, PLAN_SCROLL, PLAN_CATS,
  SELECTED_MKT_ID, REMINDERS_KEY, PROFILE_PIC,
  PROD_COL_ORDER, PROD_COL_HIDDEN, PROD_SORT, PROD_MKT_FILTER, PROD_SEARCH, PROD_EXPANDED,
  // Methods
  get, set, getBool, setBool, getFloat, setFloat, getJson, setJson, remove,
};

